import { Header } from "@/layouts/Header";
import { Footer } from "@/layouts/Footer";
import { SEOHead } from "@/features/shared/SEOHead";

const AcceptableUse = () => {
    const currentDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

    return (
        <div className="min-h-screen bg-background font-sans">
            <SEOHead
                title="Acceptable Use Policy | Frayze Technologies"
                description="Frayze's Acceptable Use Policy outlines prohibited activities, communication compliance, and fair use guidelines for our platform."
                keywords="acceptable use policy, AUP, frayze compliance, spam policy, AI ethics"
                canonical="https://frayze.ca/acceptable-use"
                robots="noindex, follow"
            />
            <Header />

            <main className="pt-40 pb-20 px-6">
                <div className="max-w-4xl mx-auto prose prose-slate dark:prose-invert">
                    <h1>Acceptable Use Policy</h1>
                    <p className="text-muted-foreground">Effective Date: {currentDate}</p>

                    <h2>1. Introduction</h2>
                    <p>
                        This Acceptable Use Policy (“AUP”) applies to all clients using Frayze systems, including CRM, automations, messaging, websites, and AI services.
                    </p>
                    <p>The goal is to protect:</p>
                    <ul>
                        <li>Platform stability</li>
                        <li>Compliance with Canadian and U.S. communication laws</li>
                        <li>Third-party integrations (Google, Meta, Twilio, etc.)</li>
                        <li>The reputation and deliverability of the Frayze ecosystem</li>
                    </ul>
                    <p>Violation of this AUP may result in suspension or termination of services.</p>

                    <h2>2. Prohibited Activities</h2>
                    <p>Clients may not use Frayze for:</p>

                    <h3>A. Spam or Unsolicited Messaging</h3>
                    <ul>
                        <li>Bulk SMS/email without permission</li>
                        <li>Purchased, scraped, or rented contact lists</li>
                        <li>Unverified imports</li>
                        <li>Cold text campaigns without consent</li>
                        <li>Auto-dialing without permission</li>
                    </ul>

                    <h3>B. Illegal, Harmful, or Deceptive Content</h3>
                    <p>Including but not limited to:</p>
                    <ul>
                        <li>Fraud</li>
                        <li>Scams</li>
                        <li>Abuse or harassment</li>
                        <li>Defamation</li>
                        <li>Malware/phishing</li>
                        <li>False medical claims</li>
                        <li>Misleading financial claims</li>
                        <li>Hate speech</li>
                        <li>Violence</li>
                    </ul>

                    <h3>C. High-Risk or Prohibited Industries</h3>
                    <p>Without written approval from Frayze:</p>
                    <ul>
                        <li>Payday loans</li>
                        <li>Cryptocurrency trading</li>
                        <li>Firearms</li>
                        <li>Adult content</li>
                        <li>CBD / cannabis</li>
                        <li>Multi-level marketing schemes</li>
                        <li>Gambling</li>
                    </ul>

                    <h3>D. Misusing AI Agents</h3>
                    <ul>
                        <li>Pretending AI agents are humans</li>
                        <li>Using AI to impersonate others</li>
                        <li>Using AI to provide legal, medical, or financial guarantees</li>
                        <li>Using AI to harass or threaten customers</li>
                        <li>Using AI to mislead or falsify information</li>
                    </ul>

                    <h3>E. System Abuse</h3>
                    <ul>
                        <li>Excessive API calls designed to overload the system</li>
                        <li>Automation loops</li>
                        <li>Unauthorized penetration testing</li>
                        <li>Attempting to access other customers’ data</li>
                        <li>Circumventing usage fees</li>
                    </ul>

                    <h2>3. Communication Compliance (CASL, TCPA, A2P)</h2>
                    <p>All clients must comply with:</p>
                    <ul>
                        <li>CASL (Canadian Anti-Spam Legislation)</li>
                        <li>A2P 10DLC rules (carrier compliance)</li>
                        <li>TCPA (Telephone Consumer Protection Act for the U.S.)</li>
                    </ul>
                    <p>This requires:</p>
                    <ul>
                        <li>Proper consent for SMS and email</li>
                        <li>Respecting opt-outs</li>
                        <li>Proper identification</li>
                        <li>No misleading messaging</li>
                        <li>Honoring unsubscribe requests within 10 days</li>
                    </ul>
                    <p>
                        Failure to comply can result in carrier blocks or legal penalties.<br />
                        Frayze is not responsible for compliance violations committed by clients.
                    </p>

                    <h2>4. Use of AI Voice & Chat Systems</h2>
                    <p>AI systems must:</p>
                    <ul>
                        <li>Identify themselves as AI if a user asks</li>
                        <li>Not be used to harass, threaten, or mislead</li>
                        <li>Not replace emergency services</li>
                        <li>Not provide legal/medical/financial advice</li>
                        <li>Not impersonate real individuals without clearly stated consent</li>
                    </ul>

                    <h2>5. Data Integrity & Security</h2>
                    <p>Users may not:</p>
                    <ul>
                        <li>Store illegal content</li>
                        <li>Import stolen or unauthorized customer data</li>
                        <li>Access or attempt to breach Frayze systems</li>
                        <li>Circumvent security or authentication measures</li>
                        <li>Scrape or extract platform data without permission</li>
                    </ul>

                    <h2>6. Fair Use Limitations</h2>
                    <p>Frayze may impose limits or suspend access if usage:</p>
                    <ul>
                        <li>Threatens platform performance</li>
                        <li>Suggests abuse or misuse</li>
                        <li>Exceeds fair operational thresholds</li>
                        <li>Impacts other clients negatively</li>
                        <li>Violates any communication compliance rules</li>
                    </ul>

                    <h2>7. Consequences of Violation</h2>
                    <p>Frayze may take the following actions:</p>
                    <ul>
                        <li>Warning notice</li>
                        <li>Temporary suspension</li>
                        <li>Blocking specific features (SMS, calls, AI, workflows)</li>
                        <li>Termination of account</li>
                        <li>Reporting violations to carriers or authorities</li>
                    </ul>
                    <p>No refunds are issued for suspensions caused by AUP violations.</p>

                    <h2>8. Updates to This Policy</h2>
                    <p>
                        Frayze may update this AUP at any time.<br />
                        Clients will be notified of major changes via email or dashboard announcement.
                    </p>

                    <h2>9. Contact</h2>
                    <p>For questions about this policy:</p>
                    <p>
                        Email: <a href="mailto:compliance@frayze.ca" className="text-primary hover:underline">compliance@frayze.ca</a><br />
                        Phone: <a href="tel:+18077000079" className="text-primary hover:underline">+1 807-700-0079</a>
                    </p>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default AcceptableUse;
